import styled from '@emotion/styled'
import { Box, Skeleton, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'

import { LoadWhenInView } from '@js/common/components/LoadWhenInView'
import { isMoreThanOneElementArray } from '@js/common/typeguards/isMoreThanOneElementArray'

import { ImageGallery } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGallery'
import {
  FocusViewImage,
  useImageGalleryContext
} from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGalleryContext'
import { IMAGE_ASPECT_RATIO } from '@f-fertighaus/applications/Next/config/constants'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { ImageSource } from '@f-fertighaus/applications/Next/types/houseDetailsResponse'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'

function useSelectedThumbnail() {
  const {
    state: { floor_plans }
  } = useHouseDetail()
  const [selectedThumbnail, setSelectedThumbnail] = React.useState(
    floor_plans && floor_plans[0]?.['fill-320x240']
  )

  const selectNewThumbnail = (thumbnail: string) => {
    webtrack({
      category: 'image',
      action: 'click',
      label: 'focus-thumbnail',
      location: 'floor-plan-gallery',
      values: [{ type: 'url', value: thumbnail || '' }]
    })
    setSelectedThumbnail(prev => (thumbnail === prev ? prev : thumbnail))
  }

  return {
    selectedThumbnail,
    selectNewThumbnail
  }
}

export const FloorPlanGalleryOverview = () => {
  const {
    state: { floor_plans }
  } = useHouseDetail()
  const { dispatch } = useImageGalleryContext()
  const { selectedThumbnail, selectNewThumbnail } = useSelectedThumbnail()

  const focusViewImageSources = floor_plans?.map(imageSource => ({
    src: imageSource['max-1600x1200.watermarked'],
    alt: imageSource.alt_text
  }))

  const handleContentClick = () => {
    dispatch({
      type: 'openFloorPlan',
      imageSources: focusViewImageSources as FocusViewImage[],
      displayImgIndex: imageSourceIndex
    })
    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'open-gallery',
      location: 'floor-plan-gallery',
      values: [{ type: 'text', value: '(ICON)' }]
    })
  }

  const handleExpandIconClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation()
    dispatch({
      type: 'openFloorPlan',
      imageSources: focusViewImageSources as FocusViewImage[],
      displayImgIndex: imageSourceIndex
    })
    webtrack({
      category: 'image',
      action: 'click',
      label: 'open-gallery',
      location: 'floor-plan-gallery',
      values: [{ type: 'text', value: '(ICON)' }]
    })
  }

  const imageSource = floor_plans?.find(
    plan => plan['fill-320x240'] === selectedThumbnail
  )

  const imageSourceIndex =
    floor_plans?.findIndex(
      plan => plan['fill-320x240'] === selectedThumbnail
    ) || 0

  return (
    <>
      {imageSource && (
        <Box sx={{ py: 1 }}>
          <Typography variant='bodyTitle' component='p'>
            {t('houseDetailPage.floor_plans.images-header')}
          </Typography>
          <LoadWhenInView Placeholder={FloorPlanPlaceholder}>
            <ImageGallery.ImageBox
              rounded
              onContentClick={handleContentClick}
              onExpandIconClick={handleExpandIconClick}>
              <LazyLoadImage
                placeholderSrc={`${imageSource['fill-320x240']}`}
                srcSet={`${imageSource['fill-320x240']} 450w,
                         ${imageSource['width-640x480']} 600w,
                         ${imageSource['width-1280x960']} 1000w,
                         ${imageSource['max-1600x1200.watermarked']} 1600w,
                         ${imageSource.original} 2048w`}
                sizes='100vw'
                style={{
                  boxShadow: '0 2px 50px 0 rgba(59, 71, 104, 0.05)',
                  aspectRatio: `${IMAGE_ASPECT_RATIO}`
                }}
                src={`${imageSource.original}`}
                alt={imageSource.alt_text}
              />
            </ImageGallery.ImageBox>
          </LoadWhenInView>
          {isMoreThanOneElementArray<ImageSource>(floor_plans) && (
            <ThumbnailWrapper>
              {floor_plans.map(plan => (
                <Thumbnail
                  onClick={() => selectNewThumbnail(plan['fill-320x240'])}
                  selected={selectedThumbnail === plan['fill-320x240']}
                  key={plan['fill-320x240']}>
                  <LoadWhenInView Placeholder={FloorPlanThumbnailPlaceholder}>
                    <img src={plan['fill-320x240']} alt={plan.alt_text} />
                  </LoadWhenInView>
                </Thumbnail>
              ))}
            </ThumbnailWrapper>
          )}
        </Box>
      )}
    </>
  )
}

interface LazyLoadImage extends React.ImgHTMLAttributes<HTMLImageElement> {
  placeholderSrc: string
  src: string
  alt: string
  srcSet: string
}

export const LazyLoadImage = ({
  placeholderSrc,
  src,
  srcSet,
  ...props
}: LazyLoadImage) => {
  const [imgSrc, setImgSrc] = useState(placeholderSrc || src)
  const [imgSrcSet, setImgSrcSet] = useState(srcSet)

  useEffect(() => {
    const img = new Image()
    img.src = src
    img.srcset = srcSet
    img.onload = () => {
      setImgSrc(src)
      setImgSrcSet(srcSet)
    }
  }, [src, srcSet])

  return (
    <ImageGallery.Image
      {...{ src: imgSrc, srcSet: imgSrcSet, ...props }}
      alt={props.alt}
    />
  )
}

const Thumbnail = styled.div<{ selected: boolean }>`
  overflow: hidden;
  border-radius: var(--border-radius-card-large);
  border: 1px solid
    ${({ selected, theme }) =>
      selected ? theme.palette.primary.active : theme.palette.stone[50]};
  width: ${({ theme }) => `calc((100% - ${theme.spacing(4)}) / 3)`};
  margin: 0 ${({ theme }) => theme.spacing(1)};
  &:first-of-type {
    margin: 0 ${({ theme }) => theme.spacing(1)} 0 0;
  }

  &:last-of-type {
    margin: 0 0 0 ${({ theme }) => theme.spacing(1)};
  }
  box-shadow: 0 2px 50px 0 rgba(59, 71, 104, 0.05);
`

const ThumbnailWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(1)} 0;
  display: flex;
  justify-content: flex-start;
`

const FloorPlanPlaceholder = () => (
  <PlaceholderWrapper>
    <StyledSkeleton />
  </PlaceholderWrapper>
)
const FloorPlanThumbnailPlaceholder = () => (
  <PlaceholderThumbnailWrapper>
    <StyledSkeleton />
  </PlaceholderThumbnailWrapper>
)

const PlaceholderWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  height: 0;
  overflow: hidden;
  padding-bottom: calc(100% / ${IMAGE_ASPECT_RATIO});
  position: relative;
`

const PlaceholderThumbnailWrapper = styled.div`
  height: 0;
  overflow: hidden;
  padding-bottom: calc(100% / ${IMAGE_ASPECT_RATIO});
  position: relative;
`

const StyledSkeleton = styled(Skeleton)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: none;
`
